/**
 * Scrolls to the first error field in a form
 * @param formId - The ID of the form to search for errors
 * @param options - Configuration options for scrolling behavior
 */
export const scrollToFirstError = (
    formId: string,
    options?: {
        offset?: number;
        behavior?: ScrollBehavior;
        errorSelectors?: string[];
    },
): void => {
    const {
        offset = 100, // Offset from top to account for fixed headers
        behavior = "smooth",
        errorSelectors = [
            '[aria-invalid="true"]',
            ".error",
            '[data-error="true"]',
            ".has-error",
            ".field-error",
            "input:invalid",
            "textarea:invalid",
            "select:invalid",
        ],
    } = options || {};

    const form = document.getElementById(formId);
    if (!form) {
        console.warn(`Form with id "${formId}" not found`);
        return;
    }

    // Build selector string
    const selector = errorSelectors.join(", ");
    const errorElements = form.querySelectorAll(selector);

    if (errorElements.length === 0) {
        // If no error indicators found, try to find error message elements
        const errorMessageElements = form.querySelectorAll(
            '[role="alert"], .error-message, [data-error-message="true"]',
        );

        if (errorMessageElements.length > 0) {
            scrollToElement(errorMessageElements[0] as HTMLElement, offset, behavior);
            return;
        }

        console.warn(`No error elements found in form "${formId}"`);
        return;
    }

    // Get the first error element
    const firstError = errorElements[0] as HTMLElement;
    scrollToElement(firstError, offset, behavior);

    // Try to focus the element if it's focusable
    if (
        firstError instanceof HTMLInputElement ||
        firstError instanceof HTMLTextAreaElement ||
        firstError instanceof HTMLSelectElement
    ) {
        firstError.focus({ preventScroll: true });
    } else {
        // Try to find a focusable element within or near the error element
        const focusableElement = firstError.querySelector("input, textarea, select") as HTMLElement;
        focusableElement?.focus({ preventScroll: true });
    }
};

/**
 * Scrolls to a specific element with offset
 */
const scrollToElement = (element: HTMLElement, offset: number, behavior: ScrollBehavior): void => {
    const elementPosition = element.getBoundingClientRect().top;
    const offsetPosition = elementPosition + window.pageYOffset - offset;

    window.scrollTo({
        top: offsetPosition,
        behavior,
    });
};

/**
 * Enhanced form validation with automatic scrolling to errors
 * @param validators - Object containing validator functions keyed by field name
 * @param formId - The ID of the form
 * @param options - Configuration options for scrolling behavior
 * @returns Object with isValid flag and array of failed field keys
 */
export const validateFormWithScroll = (
    validators: { [key: string]: (() => boolean) | undefined },
    formId: string,
    options?: Parameters<typeof scrollToFirstError>[1],
): { isValid: boolean; failedFields: string[] } => {
    const validatorEntries = Object.entries(validators);
    const failedFields: string[] = [];

    // Run all validators and collect failed fields
    validatorEntries.forEach(([key, validator]) => {
        if (validator && !validator()) {
            failedFields.push(key);
        }
    });

    const isValid = failedFields.length === 0;

    if (!isValid) {
        // Small delay to ensure error states are rendered in DOM
        setTimeout(() => {
            scrollToFirstError(formId, options);
        }, 100);
    }

    return { isValid, failedFields };
};

